import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { FileText, X, CheckCircle, MessageSquare, Image, ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';

const BASE_URL = process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:8000';

export default function ReportDetailsModal({ isOpen, onClose, report, onReview }) {
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);

  if (!isOpen || !report) return null;

  // Parse visit_photos — could be array or JSON string
  let photos = [];
  try {
    const raw = report.visit_photos;
    if (Array.isArray(raw)) photos = raw;
    else if (typeof raw === 'string' && raw.startsWith('[')) photos = JSON.parse(raw);
    else if (typeof raw === 'string' && raw.length > 0) photos = [raw];
  } catch (_) {
    photos = [];
  }

  const getPhotoUrl = (path) => `${BASE_URL}/storage/${path}`;

  const openLightbox = (idx) => setLightboxIndex(idx);
  const closeLightbox = () => setLightboxIndex(null);
  const prevPhoto = () => setLightboxIndex((i) => (i - 1 + photos.length) % photos.length);
  const nextPhoto = () => setLightboxIndex((i) => (i + 1) % photos.length);

  return createPortal(
    <>
      <div className="fixed inset-0 z-[999] flex items-center justify-center p-4">
        {/* Blurred background */}
        <div
          className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm transition-opacity"
          onClick={onClose}
        ></div>

        {/* Modal Box */}
        <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-[600px] flex flex-col max-h-[90vh] overflow-hidden animate-in fade-in zoom-in-95 duration-200">

          {/* Header */}
          <div className="px-6 py-4 border-b border-slate-100 flex items-start justify-between bg-white z-10">
            <div className="flex items-start gap-3">
              <div className="mt-1 flex-shrink-0 text-emerald-500">
                <FileText size={20} strokeWidth={2.5} />
              </div>
              <div>
                <h2 className="text-xl font-black text-slate-800 tracking-tight">Daily Work Report</h2>
                <p className="text-[13px] text-slate-500 font-medium mt-0.5 flex items-center gap-1.5 flex-wrap">
                  Submitted by <span className="font-bold text-slate-800">{report.name || report.user?.name || 'Employee'}</span>
                  <span>•</span>
                  <span className="text-blue-600 font-bold">
                    {new Date(report.date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                  </span>
                  {report.submitted_at && (
                    <>
                      <span>•</span>
                      <span className="text-slate-400 font-bold">
                        {new Date(report.submitted_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true })}
                      </span>
                    </>
                  )}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600 rounded-full transition-colors"
            >
              <X size={20} />
            </button>
          </div>

          {/* Scrollable Content */}
          <div className="flex-1 overflow-y-auto p-6 space-y-6 bg-slate-50/50 custom-scrollbar">

            {/* Main Report Container */}
            <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">

              {/* TASKS COMPLETED */}
              <div className="mb-6">
                <div className="flex items-center gap-1.5 mb-2">
                  <CheckCircle size={14} className="text-emerald-500" strokeWidth={3} />
                  <span className="text-[10px] font-black tracking-widest uppercase text-slate-400">TASKS COMPLETED</span>
                </div>
                <div className="bg-slate-50 border border-slate-100 rounded-lg p-3 text-[13px] font-medium text-slate-700 leading-relaxed shadow-inner whitespace-pre-wrap">
                  {report.tasks_completed || report.tasksCompleted || "No tasks reported."}
                </div>
              </div>

              {/* Grid Stats */}
              <div className="grid grid-cols-2 gap-y-6 gap-x-4">
                <div>
                  <span className="text-[10px] font-black tracking-widest uppercase text-slate-400 block mb-1">TOTAL ADMISSIONS</span>
                  <span className="text-xl font-black text-emerald-600">{report.total_admissions ?? report.admissions ?? 0}</span>
                </div>

                <div>
                  <span className="text-[10px] font-black tracking-widest uppercase text-indigo-400 block mb-1">TOMORROW'S PLAN</span>
                  <p className="text-[12px] font-bold text-slate-700 whitespace-pre-wrap">
                    {report.tomorrow_plan || report.tomorrowPlan || "-"}
                  </p>
                </div>

                <div>
                  <span className="text-[10px] font-black tracking-widest uppercase text-blue-400 block mb-1">IN PROGRESS</span>
                  <p className="text-[12px] font-bold text-slate-700">{report.tasks_in_progress || report.inProgress || "-"}</p>
                </div>

                <div>
                  <span className="text-[10px] font-black tracking-widest uppercase text-slate-400 block mb-1">NOTES</span>
                  <p className="text-[12px] font-bold text-slate-700">{report.notes || "-"}</p>
                </div>


              </div>
            </div>

            {/* ── VISIT PHOTOS SECTION ── */}
            {photos.length > 0 && (
              <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
                <div className="flex items-center gap-2 mb-4">
                  <Image size={15} className="text-purple-500" strokeWidth={2.5} />
                  <span className="text-[10px] font-black tracking-widest uppercase text-purple-400">
                    VISIT PHOTOS ({photos.length})
                  </span>
                </div>
                <div className="grid grid-cols-4 gap-2">
                  {photos.map((path, idx) => (
                    <div
                      key={idx}
                      className="relative group cursor-pointer aspect-square rounded-lg overflow-hidden border border-slate-200 shadow-sm hover:shadow-md transition-all hover:scale-105"
                      onClick={() => openLightbox(idx)}
                    >
                      <img
                        src={getPhotoUrl(path)}
                        alt={`Visit photo ${idx + 1}`}
                        className="w-full h-full object-cover"
                        onError={(e) => { e.target.src = 'https://placehold.co/100x100/f1f5f9/94a3b8?text=No+Img'; }}
                      />
                      {/* Hover overlay */}
                      <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                        <span className="text-white text-[10px] font-bold">View</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Feedback Section */}
            <div>
              <div className="flex items-center gap-2 mb-2">
                <MessageSquare size={16} className="text-blue-500" />
                <span className="text-[12px] font-bold text-slate-700">Manager Feedback / Review</span>
              </div>
              <textarea
                rows="3"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Write your feedback or acknowledge the report..."
              className="w-full border border-slate-200 rounded-lg p-3 text-[13px] focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-colors shadow-sm resize-y"
              ></textarea>
            </div>

          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-slate-100 bg-white flex items-center justify-end gap-3 z-10">
            <button
              onClick={onClose}
            className="px-4 py-2 font-bold text-[13px] text-slate-600 bg-slate-50 border border-slate-200 rounded-lg hover:bg-slate-100 transition-colors shadow-sm"
          >
            Close
          </button>
          <button
            disabled={submitting || report?.status === 'reviewed'}
            onClick={async () => {
              if (!onReview) return;
              setSubmitting(true);
              await onReview(report.id, comment);
              setComment('');
              setSubmitting(false);
            }}
            className={`px-5 py-2 font-bold text-[13px] text-white rounded-lg shadow-sm transition-all flex items-center gap-2 ${
              report?.status === 'reviewed'
                ? 'bg-slate-400 cursor-not-allowed'
                : 'bg-blue-600 hover:bg-blue-700 shadow-blue-600/20'
            }`}
          >
            {submitting ? (
              <><Loader2 size={14} className="animate-spin" /> Saving...</>
            ) : report?.status === 'reviewed' ? (
              <><CheckCircle size={14} /> Already Reviewed</>
            ) : (
              'Mark as Reviewed'
            )}
          </button>
          </div>

        </div>
      </div>

      {/* ── LIGHTBOX POPUP ── */}
      {lightboxIndex !== null && (
        <div
          className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/90 backdrop-blur-sm"
          onClick={closeLightbox}
        >
          {/* Close button */}
          <button
            className="absolute top-4 right-4 text-white/70 hover:text-white bg-white/10 hover:bg-white/20 p-2 rounded-full transition-all z-10"
            onClick={closeLightbox}
          >
            <X size={22} />
          </button>

          {/* Photo counter */}
          <div className="absolute top-4 left-1/2 -translate-x-1/2 text-white/70 text-[12px] font-bold bg-black/40 px-3 py-1 rounded-full">
            {lightboxIndex + 1} / {photos.length}
          </div>

          {/* Prev button */}
          {photos.length > 1 && (
            <button
              className="absolute left-4 top-1/2 -translate-y-1/2 text-white/70 hover:text-white bg-white/10 hover:bg-white/20 p-2 rounded-full transition-all z-10"
              onClick={(e) => { e.stopPropagation(); prevPhoto(); }}
            >
              <ChevronLeft size={28} />
            </button>
          )}

          {/* Main image */}
          <img
            src={getPhotoUrl(photos[lightboxIndex])}
            alt={`Visit photo ${lightboxIndex + 1}`}
            className="max-w-[90vw] max-h-[85vh] object-contain rounded-lg shadow-2xl"
            onClick={(e) => e.stopPropagation()}
            onError={(e) => { e.target.src = 'https://placehold.co/400x400/1e293b/94a3b8?text=Image+Not+Found'; }}
          />

          {/* Next button */}
          {photos.length > 1 && (
            <button
              className="absolute right-4 top-1/2 -translate-y-1/2 text-white/70 hover:text-white bg-white/10 hover:bg-white/20 p-2 rounded-full transition-all z-10"
              onClick={(e) => { e.stopPropagation(); nextPhoto(); }}
            >
              <ChevronRight size={28} />
            </button>
          )}
        </div>
      )}
    </>,
    document.body
  );
}
